import React from 'react';
import { User, Users, Code, Briefcase, Heart, Mail, Phone, Globe, Github, Linkedin, Award, Settings } from 'lucide-react';

const AboutPage = ({ onNavigate, isAdmin }) => {
  const teamMembers = [
    {
      name: 'Collin Jensen',
      role: 'Lead Developer',
      bio: 'Full-stack developer with a passion for the marine industry. Specializes in creating innovative digital solutions for boat dealers and manufacturers.',
      expertise: ['React Development', 'API Integration', 'Marine Industry Tech']
    },
    {
      name: 'Alex',
      role: 'Creative Director',
      bio: 'Leads our creative vision and brand strategy initiatives. Brings extensive experience in visual design and user experience for marine industry clients.',
      expertise: ['Brand Strategy', 'Visual Design', 'UX/UI']
    },
    {
      name: 'Tom',
      role: 'Marketing Strategist',
      bio: 'Develops and executes digital marketing campaigns that drive results. Expert in SEO, paid advertising, and content marketing for marine businesses.',
      expertise: ['Digital Marketing', 'SEO', 'Content Strategy']
    }
  ];

  return (
    <div className="about-page">
      <div className="page-header about-header">
        <h1>About the Team</h1>
        <p>The vision and development behind this Centurion Boats inventory system</p>
        {isAdmin && (
          <button className="admin-quick-access" onClick={() => onNavigate('admin')}>
            <Settings size={18} />
            Admin Dashboard
          </button>
        )}
      </div>

      {/* Team Section */}
      <div className="team-section">
        <h2 className="section-title">
          <Users size={24} />
          Meet the Team
        </h2>
        <div className="team-grid">
          {teamMembers.map((member, index) => (
            <div key={index} className="team-card">
              <div className="team-avatar">
                <User size={60} />
              </div>
              <h3>{member.name}</h3>
              <p className="team-role">{member.role}</p>
              <p className="team-bio">{member.bio}</p>
              <div className="expertise-tags">
                {member.expertise.map((skill, idx) => (
                  <span key={idx} className="expertise-tag">{skill}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="about-sections">
        {/* Project Overview */}
        <div className="about-section">
          <div className="section-icon">
            <Code size={32} />
          </div>
          <div className="section-content">
            <h3>About This Project</h3>
            <p>
              This inventory system was built to provide a seamless experience for browsing Centurion's 
              nationwide inventory. The platform features real-time data synchronization with Google Sheets, 
              allowing for instant updates and accurate availability information.
            </p>
            <ul className="feature-list">
              <li>Real-time inventory tracking from multiple data sources</li>
              <li>Interactive map view showing boat locations across the US</li>
              <li>Advanced filtering and search capabilities</li>
              <li>Responsive design for all devices</li>
              <li>Dealer network integration</li>
            </ul>
          </div>
        </div>

        {/* Technical Stack */}
        <div className="about-section">
          <div className="section-icon">
            <Briefcase size={32} />
          </div>
          <div className="section-content">
            <h3>Technical Excellence</h3>
            <p>
              Built with cutting-edge technologies to ensure performance, reliability, and scalability:
            </p>
            <div className="tech-stack">
              <div className="tech-item">React.js</div>
              <div className="tech-item">Node.js</div>
              <div className="tech-item">Express</div>
              <div className="tech-item">Google Sheets API</div>
              <div className="tech-item">Leaflet Maps</div>
              <div className="tech-item">Modern CSS</div>
              <div className="tech-item">RESTful APIs</div>
            </div>
          </div>
        </div>

        {/* Vision */}
        <div className="about-section">
          <div className="section-icon">
            <Heart size={32} />
          </div>
          <div className="section-content">
            <h3>Vision & Mission</h3>
            <p>
              My vision is to transform the boat shopping experience by providing transparent, 
              accessible, and comprehensive inventory information. This platform empowers both 
              dealers and customers with the tools they need to make informed decisions.
            </p>
            <div className="vision-points">
              <div className="vision-item">
                <Award size={20} />
                <span>Simplify the boat shopping experience</span>
              </div>
              <div className="vision-item">
                <Award size={20} />
                <span>Connect customers with their dream boats</span>
              </div>
              <div className="vision-item">
                <Award size={20} />
                <span>Support dealers with powerful inventory tools</span>
              </div>
              <div className="vision-item">
                <Award size={20} />
                <span>Showcase Centurion's exceptional product line</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="about-section">
          <div className="section-icon">
            <Mail size={32} />
          </div>
          <div className="section-content">
            <h3>Get In Touch</h3>
            <p>
              I'm always interested in new opportunities and collaborations. Whether you have 
              questions about this platform, need custom development work, or want to discuss 
              marine industry technology, feel free to reach out.
            </p>
            <div className="contact-info">
              <a href="mailto:collin@example.com" className="contact-link">
                <Mail size={18} />
                collin@example.com
              </a>
              <a href="tel:8015551234" className="contact-link">
                <Phone size={18} />
                (801) 555-1234
              </a>
              <a href="https://collinjensen.dev" target="_blank" rel="noopener noreferrer" className="contact-link">
                <Globe size={18} />
                collinjensen.dev
              </a>
              <a href="https://github.com/collinjensen" target="_blank" rel="noopener noreferrer" className="contact-link">
                <Github size={18} />
                GitHub
              </a>
              <a href="https://linkedin.com/in/collinjensen" target="_blank" rel="noopener noreferrer" className="contact-link">
                <Linkedin size={18} />
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonial/Quote */}
      <div className="creator-quote">
        <blockquote>
          "Technology should enhance, not complicate. This platform embodies that philosophy, 
          making the complex world of boat inventory simple and accessible for everyone."
        </blockquote>
        <cite>- Collin Jensen</cite>
      </div>

      {/* Footer CTA */}
      <div className="about-cta">
        <h3>Interested in Similar Solutions?</h3>
        <p>I specialize in creating custom inventory and management systems for the marine industry.</p>
        <button className="cta-button">Let's Discuss Your Project</button>
      </div>
    </div>
  );
};

export default AboutPage;
